/**
 * SMS lines — `/api/admin/v2/messaging/sms-lines`.
 *
 * A line is one SIM in one of our Android phones (SMSGate relay), or the
 * built-in Hubtel sender, which is one-way: an alphanumeric ID nobody can
 * reply to. Lines are what SMS is sent from and what inbound SMS arrives on,
 * as mailboxes are for email.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';

export interface SmsLine {
  id: string;
  label: string;
  provider: 'hubtel' | 'smsgate';
  /** E.164 of the SIM; null for Hubtel and for a line whose number was not entered. */
  phone: string | null;
  device_id: string | null;
  /** 1-3, or null for the phone's default SIM. */
  sim_number: number | null;
  enabled: boolean;
  /** False for Hubtel: replies to it go nowhere. */
  two_way: boolean;
  /** The line a reply uses when its conversation is not on one yet. */
  default: boolean;
}

/** A phone registered on the relay. */
export interface SmsPhone {
  id: string;
  name?: string;
  lastSeen?: string;
  createdAt?: string;
}

type Envelope<T> = { status: number; data: T };
const BASE = '/api/admin/v2/messaging/sms-lines';
export const SMS_LINES_KEY = ['inbox', 'sms-lines'] as const;

export const useSmsLinesQuery = () => {
  const queryFn = useGetQueryFn<Envelope<{ lines: SmsLine[]; phones: SmsPhone[]; relay_error?: string | null }>>({
    defaultErrorMessage: 'Could not load the SMS lines',
    endpoint: BASE,
  });
  return useQuery({
    queryKey: SMS_LINES_KEY,
    queryFn: () => queryFn(),
    staleTime: 30_000,
    select: (response) => response?.data,
  });
};

export type SmsLineInput = {
  id: string;
  label: string;
  phone?: string;
  device_id: string;
  sim_number?: number | null;
  enabled?: boolean;
  default?: boolean;
};

export const useSaveSmsLineMutation = () => {
  const mutationFn = useGetMutationFn<SmsLineInput, Envelope<{ line: SmsLine }>>({
    defaultErrorMessage: 'The line was not saved',
    endpoint: ({ id }) => `${BASE}/${encodeURIComponent(id)}`,
    method: 'PUT',
  });
  return useMutation({ mutationFn });
};

export const useDeleteSmsLineMutation = () => {
  const mutationFn = useGetMutationFn<{ id: string }, Envelope<{ success: boolean }>>({
    defaultErrorMessage: 'The line was not removed',
    endpoint: ({ id }) => `${BASE}/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
  return useMutation({ mutationFn });
};

export const useRemoveSmsPhoneMutation = () => {
  const mutationFn = useGetMutationFn<{ device_id: string }, Envelope<{ success: boolean }>>({
    defaultErrorMessage: 'The phone was not removed',
    endpoint: ({ device_id }) => `${BASE}/phones/${encodeURIComponent(device_id)}`,
    method: 'DELETE',
  });
  return useMutation({ mutationFn });
};

export const useConnectSmsWebhooksMutation = () => {
  const mutationFn = useGetMutationFn<void, Envelope<{ success: boolean; url: string; added: string[] }>>({
    defaultErrorMessage: 'The phones were not connected',
    endpoint: `${BASE}/webhooks`,
    method: 'POST',
  });
  return useMutation({ mutationFn });
};

/** "Accra 1 (+233...)" for a select; Hubtel says it takes no replies. */
export const smsLineLabel = (l: SmsLine) =>
  l.two_way ? `${l.label}${l.phone ? ` (${l.phone})` : ''}` : `${l.label} (one-way, replies are not collected)`;
